import { Box, CircularProgress, LinearProgress, Link, Stack, Typography, useTheme } from '@mui/material';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import CircleIcon from '@mui/icons-material/Circle';
import AlertTriangle from '../assets/icons/alert-triangle.svg?react';
import { useLocation } from 'react-router-dom';
import { SectionType } from '../routes';
import { ApplicationProgress } from '../contexts/ApplicationContext/ApplicationContext';
import { StudentApplicationValues } from '../student/application/StudentApplication';

interface MenuItemProps {
  href?: string;
  label: string;
  pageProgress?: ApplicationProgress<StudentApplicationValues>;
  /* last in the list renders a bottom border */
  last?: boolean;
  sections?: SectionType[];
  displayErrors: boolean;
}
export const MenuItem = ({ href, label, pageProgress, last = false, sections, displayErrors }: MenuItemProps) => {
  const { pathname } = useLocation();
  const isActive = href === pathname;
  const theme = useTheme();
  const iconStyles = { position: 'absolute', left: isActive ? theme.spacing(1) : theme.spacing(2), width: theme.spacing(2), height: theme.spacing(2) };
  let progressIcon = <></>;
  if (pageProgress !== undefined) {
    if (pageProgress.progress === 100) {
      progressIcon = <TaskAltIcon sx={{ color: theme.palette.success.dark, ...iconStyles }} />;
    } else if (!displayErrors) {
      progressIcon = (
        <>
          <CircularProgress variant="determinate" value={100} size={theme.spacing(2)} sx={{ color: theme.palette.grey[50], ...iconStyles }} />
          <CircularProgress
            variant="determinate"
            value={pageProgress.progress}
            size={theme.spacing(2)}
            thickness={4}
            sx={{ color: theme.palette.primary.main, ...iconStyles }}
          />
        </>
      );
    } else {
      progressIcon = (
        <Box sx={iconStyles} color={theme.palette.warning.main}>
          <AlertTriangle data-testid="AlertTriangle" />
        </Box>
      );
    }
  }
  const borderString = `1px solid ${isActive ? theme.palette.primary[500] : theme.palette.primary[300]}`;
  const sideBorderString = isActive ? `${theme.spacing(1)} solid ${theme.palette.primary[500]}` : 0;

  const showStepList = sections && pageProgress && isActive;

  return (
    <Stack>
      <Link
        href={href}
        sx={{
          height: theme.spacing(6),
          borderTop: borderString,
          borderBottom: last || showStepList ? borderString : 0,
          borderLeft: sideBorderString,
          borderRight: sideBorderString,
          textAlign: 'center',
          '&:hover': { color: theme.palette.primary.main },
        }}
      >
        <Stack direction="row" position="relative" height="100%" alignItems="center">
          {progressIcon}
          <Typography color="primary" sx={{ width: '100%' }}>
            {label}
          </Typography>
        </Stack>
      </Link>
      {showStepList && <SectionsList sections={sections} sectionsProgress={pageProgress} displayErrors={displayErrors} />}
    </Stack>
  );
};

interface SectionsListProps {
  sections: SectionType[];
  sectionsProgress: ApplicationProgress<StudentApplicationValues>;
  displayErrors: boolean;
}

const SectionsList = ({ sections, sectionsProgress, displayErrors }: SectionsListProps) => {
  const theme = useTheme();

  return (
    <Stack sx={{ px: 2, my: 1, position: 'relative' }}>
      <Box
        sx={{
          position: 'absolute',
          left: theme.spacing(3),
          top: 0,
          bottom: 0,
          height: '100%',
          width: theme.spacing(0.5),
          bgcolor: theme.palette.grey[100],
          borderRadius: theme.spacing(1),
        }}
      />
      {sections.map(({ label, key }) => {
        const progress = sectionsProgress[key as keyof StudentApplicationValues]?.progress;
        // TODO: add logic to keep track of the user's section based on scroll and determine if the section is active
        return <SectionItem key={label} progress={progress ?? 0} label={label} active={false} displayErrors={displayErrors} />;
      })}
    </Stack>
  );
};

interface SectionItemProps {
  progress: number;
  label: string;
  active: boolean;
  displayErrors: boolean;
}

const SectionItem = ({ progress, label, active, displayErrors }: SectionItemProps) => {
  const theme = useTheme();
  const iconStyles = { width: theme.spacing(2), height: theme.spacing(2), ml: '2px', mt: '2px' };
  let icon;

  if (progress === 100) {
    icon = <TaskAltIcon sx={{ color: theme.palette.success.dark, background: 'white', ...iconStyles }} />;
  } else if (!displayErrors) {
    icon = (
      <CircleIcon
        sx={{
          ...iconStyles,
          color: active ? theme.palette.primary.main : 'white',
          background: active ? 'none' : 'white',
          border: active ? 'none' : `2px solid ${theme.palette.grey[300]}`,
          borderRadius: '50%',
        }}
      />
    );
  } else {
    icon = (
      <Box sx={{ ...iconStyles, background: 'white', borderRadius: '50%' }} color={theme.palette.warning.main}>
        <AlertTriangle />
      </Box>
    );
  }

  const status = progress === 100 ? 'completed' : progress > 0 ? 'started' : 'initial';

  const linearProgressStyles = {
    height: 6,
    borderRadius: theme.spacing(1),
    mt: 0.5,
    backgroundColor: status === 'initial' && !active ? theme.palette.grey[100] : 'white',
    boxShadow: active ? `0px 0px 0px 4px ${theme.palette.primary[50]}` : 'none',
    border: active && status !== 'completed' ? `1px solid ${theme.palette.primary[500]}` : 'none',
    '& .MuiLinearProgress-bar': {
      borderRadius: theme.spacing(1),
      backgroundColor: active ? theme.palette.success.dark : theme.palette.success.main,
    },
  };

  return (
    <Stack sx={{ zIndex: 1, py: 1 }} direction="row" gap={1}>
      {icon}
      <Box flex={1}>
        <Typography variant="body2">{label}</Typography>
        <LinearProgress variant="determinate" value={progress} sx={linearProgressStyles} />
      </Box>
    </Stack>
  );
};
